import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './icon.js';
import { cx } from '../lib/cx.js';
import { newId } from '@dintiko/shared';
import type { StatusTone } from './status.js';

export interface Toast {
  id: string;
  tone: StatusTone;
  message: string;
  /** A single follow-up, e.g. "Reconnect" on an expired login. */
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  show: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <ToastProvider>');
  return value;
}

const icons: Record<StatusTone, IconName> = {
  ok: 'check',
  warn: 'alert',
  bad: 'cross',
  busy: 'dot',
  idle: 'dash',
};

const toneClasses: Record<StatusTone, string> = {
  ok: 'border-ok/40 text-ok',
  warn: 'border-warn/40 text-warn',
  bad: 'border-bad/40 text-bad',
  busy: 'border-accent/40 text-accent',
  idle: 'border-line text-muted',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = newId();
      setToasts((current) => [...current, { ...toast, id }]);
      // Errors stay until dismissed: a failed publish is not something to miss
      // because you looked away (PLAN §13.1 asks for plain, actionable messages).
      if (toast.tone !== 'bad') {
        window.setTimeout(() => dismiss(id), 5000);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* polite, not assertive: a published toast should not interrupt typing */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-3"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              'aw-toast pointer-events-auto flex w-full max-w-sm items-start gap-2.5',
              'rounded-aw border bg-surface px-3 py-2.5 shadow-aw',
              toneClasses[toast.tone],
            )}
          >
            <Icon name={icons[toast.tone]} size={15} className="mt-px shrink-0" />
            <p className="flex-1 text-sm leading-snug text-ink">{toast.message}</p>
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick();
                  dismiss(toast.id);
                }}
                className="shrink-0 text-xs font-semibold text-accent underline underline-offset-2"
              >
                {toast.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
              className="shrink-0 px-1 text-faint hover:text-ink"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
